import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { createClerkSupabaseClient } from '../../../lib/supabase.js';
import { formatNumber } from '../../../styles/tokens/formatters';
import WalletStageCard from './components/WalletStageCard.jsx';
import DaysSegmentedControl, { DAY_RANGE_OPTIONS } from './components/DaysSegmentedControl.jsx';
import { subDays, startOfDay, endOfDay } from 'date-fns';

const ConversionesMobileHome = () => {
  const { session } = useSession();
  const supabase = useMemo(() => createClerkSupabaseClient(session), [session]);
  const navigate = useNavigate();

  const [counts, setCounts] = useState({
    vistas: 0,
    sesiones: 0,
    simulaciones: 0,
    contrataciones: 0,
    suscripciones: 0,
    certificados: 0
  });
  const [weeklyGrowth, setWeeklyGrowth] = useState({
    vistas: 0,
    sesiones: 0,
    simulaciones: 0,
    contrataciones: 0,
    suscripciones: 0,
    certificados: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStageKey, setExpandedStageKey] = useState(null);
  const [rangeKey, setRangeKey] = useState('30d');

  const selectedRange = useMemo(
    () => DAY_RANGE_OPTIONS.find((option) => option.key === rangeKey) || DAY_RANGE_OPTIONS[2],
    [rangeKey]
  );

  const rangeDays = selectedRange?.days || 30;

  useEffect(() => {
    if (!session) return;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const today = new Date();
        const rangeEnd = endOfDay(today);
        const rangeStart = startOfDay(subDays(rangeEnd, rangeDays - 1));
        const prevRangeEnd = endOfDay(subDays(rangeStart, 1));
        const prevRangeStart = startOfDay(subDays(rangeStart, rangeDays));

        const getCountInRange = async (table, start, end, withSimulacion = false) => {
          let query = supabase
            .from(table)
            .select('id', { count: 'exact', head: true })
            .gte('created_at', start.toISOString())
            .lte('created_at', end.toISOString());
          if (withSimulacion) {
            query = query.eq('is_simulacion', true);
          }
          const { count, error: countError } = await query;
          if (countError) throw countError;
          return count || 0;
        };

        // Fetch Total Counts
        const [vis, ses, sim, con, sus, cer] = await Promise.all([
          getCountInRange('sesiones', rangeStart, rangeEnd),
          getCountInRange('sesiones_funnel', rangeStart, rangeEnd, true),
          getCountInRange('company_cotizaciones', rangeStart, rangeEnd),
          getCountInRange('company_contrataciones', rangeStart, rangeEnd),
          getCountInRange('company_suscripciones', rangeStart, rangeEnd),
          getCountInRange('company_certificados', rangeStart, rangeEnd)
        ]);

        setCounts({
          vistas: vis,
          sesiones: ses,
          simulaciones: sim,
          contrataciones: con,
          suscripciones: sus,
          certificados: cer
        });

        // Helper to calculate growth
        const getGrowth = async (table, withSimulacion = false) => {
          const [current, previous] = await Promise.all([
            getCountInRange(table, rangeStart, rangeEnd, withSimulacion),
            getCountInRange(table, prevRangeStart, prevRangeEnd, withSimulacion)
          ]);

          if (previous === 0) return current > 0 ? 100 : 0;
          return ((current - previous) / previous) * 100;
        };

        const [gVis, gSes, gSim, gCon, gSus, gCer] = await Promise.all([
            getGrowth('sesiones'),
            getGrowth('sesiones_funnel', true),
            getGrowth('company_cotizaciones'),
            getGrowth('company_contrataciones'),
            getGrowth('company_suscripciones'),
            getGrowth('company_certificados')
        ]);

        setWeeklyGrowth({
          vistas: gVis,
          sesiones: gSes,
          simulaciones: gSim,
          contrataciones: gCon,
          suscripciones: gSus,
          certificados: gCer
        });

      } catch (e) {
        setError(e?.message || 'Error cargando Conversiones');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [session, supabase, rangeDays]);

  const rateVistasToSesiones = counts.vistas > 0 ? (counts.sesiones / counts.vistas) * 100 : 0;
  const rateSesionesToSimulaciones = counts.sesiones > 0 ? (counts.simulaciones / counts.sesiones) * 100 : 0;
  const rateSimulacionesToContrataciones = counts.simulaciones > 0 ? (counts.contrataciones / counts.simulaciones) * 100 : 0;
  const rateContratacionesToSuscripciones = counts.contrataciones > 0 ? (counts.suscripciones / counts.contrataciones) * 100 : 0;
  const rateSuscripcionesToCertificados = counts.suscripciones > 0 ? (counts.certificados / counts.suscripciones) * 100 : 0;

  const stages = useMemo(
    () => [
      {
        stageKey: 'vistas',
        title: 'Vistas',
        subtitle: 'Entrada',
        value: formatNumber(counts.vistas),
        growth: weeklyGrowth.vistas,
        to: '/conversiones/m/vistas'
      },
      {
        stageKey: 'sesiones',
        title: 'Sesiones',
        subtitle: '% sobre vistas',
        value: formatNumber(counts.sesiones),
        rateText: `${rateVistasToSesiones.toFixed(1)}%`,
        growth: weeklyGrowth.sesiones,
        to: '/conversiones/m/sesiones'
      },
      {
        stageKey: 'simulaciones',
        title: 'Simulaciones',
        subtitle: '% sobre sesiones',
        value: formatNumber(counts.simulaciones),
        rateText: `${rateSesionesToSimulaciones.toFixed(1)}%`,
        growth: weeklyGrowth.simulaciones,
        to: '/conversiones/m/simulaciones'
      },
      {
        stageKey: 'contrataciones',
        title: 'Contrataciones',
        subtitle: '% sobre simulaciones',
        value: formatNumber(counts.contrataciones),
        rateText: `${rateSimulacionesToContrataciones.toFixed(1)}%`,
        growth: weeklyGrowth.contrataciones,
        to: '/conversiones/m/contrataciones'
      },
      {
        stageKey: 'suscripciones',
        title: 'Suscripciones',
        subtitle: '% sobre contrataciones',
        value: formatNumber(counts.suscripciones),
        rateText: `${rateContratacionesToSuscripciones.toFixed(1)}%`,
        growth: weeklyGrowth.suscripciones,
        to: '/conversiones/m/suscripciones'
      },
      {
        stageKey: 'certificados',
        title: 'Certificados',
        subtitle: '% sobre suscripciones',
        value: formatNumber(counts.certificados),
        rateText: `${rateSuscripcionesToCertificados.toFixed(1)}%`,
        growth: weeklyGrowth.certificados,
        to: '/conversiones/m/certificados'
      }
    ],
    [
      counts,
      weeklyGrowth,
      rateContratacionesToSuscripciones,
      rateSesionesToSimulaciones,
      rateSimulacionesToContrataciones,
      rateSuscripcionesToCertificados,
      rateVistasToSesiones
    ]
  );

  const handleStagePress = (stageKey, to) => {
    if (expandedStageKey !== stageKey) {
      setExpandedStageKey(stageKey);
      return;
    }

    navigate(to);
  };

  const handleBackgroundClick = () => {
    if (expandedStageKey) {
      setExpandedStageKey(null);
    }
  };

  return (
    <div 
      className="h-screen w-full bg-[#0B0B0F] text-white overflow-hidden flex flex-col relative"
      onClick={handleBackgroundClick}
    >
      <div className="mx-auto w-full max-w-[520px] px-4 pt-6 pb-4 z-50 bg-[#0B0B0F]/90 backdrop-blur-sm shrink-0">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold leading-tight">Conversiones</h1>
            <div className="text-xs text-white/70 mt-1">Resumen por etapas</div>
          </div>
        </div>
        <div className="mt-4">
          <DaysSegmentedControl value={rangeKey} onChange={setRangeKey} />
        </div>
      </div>

      <div className="flex-1 relative w-full max-w-[520px] mx-auto">
        {loading ? (
          <div className="mt-6 px-4 text-sm text-white/70">Cargando...</div>
        ) : error ? (
          <div className="mt-6 px-4 text-sm text-red-300">{error}</div>
        ) : (
          <div className="absolute inset-0 w-full h-full">
            {stages.map((stage, idx) => {
              const isSelected = expandedStageKey === stage.stageKey;
              const isAnyExpanded = !!expandedStageKey;

              let style = {};
              if (isAnyExpanded) {
                if (isSelected) {
                  style = { 
                    top: '20px',
                    zIndex: 50,
                    transform: 'scale(1)',
                    opacity: 1
                  };
                } else {
                  // Cuando hay una seleccionada, las demás se apilan abajo con un pequeño desfase
                  // para que parezcan un mazo de cartas y no una sola.
                  const offset = idx * 10; // 10px de diferencia entre cada una
                  style = { 
                    top: `calc(100% - ${120 - offset}px)`, 
                    zIndex: idx,
                    transform: `scale(${0.9 + (idx * 0.01)})`, // Pequeña variación de escala también
                    opacity: 1
                  };
                }
              } else {
                const reverseIdx = stages.length - 1 - idx;
                const spacing = 65;
                const bottomOffset = 220;
                
                style = {
                  top: `calc(100% - ${(reverseIdx * spacing) + bottomOffset}px)`,
                  zIndex: idx + 10,
                  transform: 'scale(1)',
                  opacity: 1
                };
              }

              return (
                <div
                  key={stage.stageKey}
                  className="absolute left-0 right-0 px-4 transition-all duration-500 cubic-bezier(0.32, 0.72, 0, 1)"
                  style={style}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStagePress(stage.stageKey, stage.to);
                  }}
                >
                  <WalletStageCard
                    stageKey={stage.stageKey}
                    title={stage.title}
                    subtitle={stage.subtitle}
                    value={stage.value}
                    rateText={stage.rateText}
                    growth={stage.growth}
                    isPrimary={idx === 0}
                    isExpanded={isSelected}
                    onPress={() => {}}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversionesMobileHome;
